import { ICourseRepository } from '../interfaces/ICourseRepository.js';
import { IProgressRepository } from '../interfaces/IProgressRepository.js';
import { ISessionRepository } from '../interfaces/ISessionRepository.js';
import { IUserRepository } from '../interfaces/IUserRepository.js';
import { Course, Section, Question, Session, User, UserProgress } from '@core/models';

const AUTH_STORAGE_KEY = 'adaptive-lms-auth-user';

const normalizeBaseUrl = (value) => {
  if (!value) return '';

  let normalized = String(value).trim();
  if (!normalized) return '';

  if (/^api(\/|$)/i.test(normalized)) {
    normalized = `/${normalized}`;
  }

  if (normalized.startsWith('/')) {
    normalized = normalized.replace(/\/$/, '');
    normalized = normalized.replace(/\/api\/v1\/adaptive$/i, '/api');
    return normalized;
  }

  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `https://${normalized}`;
  }

  try {
    const parsed = new URL(normalized);
    const isLocalHost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    if (parsed.protocol === 'http:' && !isLocalHost) {
      parsed.protocol = 'https:';
      normalized = parsed.toString();
    }
  } catch {
    // Ignore parsing issues and let downstream fetch surface a clear error
  }

  normalized = normalized.replace(/\/api\/v1\/adaptive\/?$/i, '');
  normalized = normalized.replace(/\/$/, '');
  return normalized;
};

const getApiBaseUrl = () => {
  const isVercelFrontend = typeof window !== 'undefined'
    && /(?:^|\.)vercel\.app$/i.test(window.location.hostname || '');

  const explicitBase = normalizeBaseUrl(import.meta.env.VITE_API_BASE_URL);
  if (explicitBase) {
    if (isVercelFrontend && !explicitBase.startsWith('/')) {
      return '/api';
    }
    return explicitBase;
  }

  if (isVercelFrontend) return '/api';

  const adaptiveBase = normalizeBaseUrl(import.meta.env.VITE_ADAPTIVE_API_BASE_URL);
  if (!adaptiveBase) return '';

  return adaptiveBase;
};

const getActorUserId = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    const normalizedActorUserId = String(parsed?.id || '').trim();
    return normalizedActorUserId || null;
  } catch {
    return null;
  }
};

class BaseApiRepository {
  constructor(entity) {
    this.entity = entity;
    this.baseUrl = getApiBaseUrl();
  }

  async _invoke(method, payload = {}) {
    if (!this.baseUrl) {
      throw new Error('API base URL is not configured. Set VITE_API_BASE_URL to /api (Vercel proxy) or a backend URL.');
    }

    const endpoint = this.baseUrl.startsWith('/')
      ? `${this.baseUrl}/v1/repository/${this.entity}/${method}`
      : new URL(`/api/v1/repository/${this.entity}/${method}`, this.baseUrl).toString();

    const actorUserId = getActorUserId();
    const requestPayload = actorUserId && payload?.actorUserId === undefined
      ? { ...payload, actorUserId }
      : payload;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload),
    });

    let json = {};
    try {
      json = await response.json();
    } catch {
      json = {};
    }

    if (!response.ok || json.success === false) {
      throw new Error(json.error || `Repository API failed: ${this.entity}.${method}`);
    }

    return json.data;
  }
}

export class APICourseRepository extends ICourseRepository {
  constructor() {
    super();
    this.client = new BaseApiRepository('course');
    this.courseCache = new Map();
    this.inFlightCourseRequests = new Map();
    this.courseCacheTtlMs = 15000;
  }

  _getCachedCourse(courseId) {
    const cached = this.courseCache.get(courseId);
    if (!cached) return null;

    if ((Date.now() - cached.fetchedAt) > this.courseCacheTtlMs) {
      this.courseCache.delete(courseId);
      return null;
    }

    return cached.course;
  }

  _setCachedCourse(courseId, course) {
    if (!courseId || !course) return;
    this.courseCache.set(courseId, {
      course,
      fetchedAt: Date.now(),
    });
  }

  _invalidateCourse(courseId) {
    if (courseId) {
      this.courseCache.delete(courseId);
      this.inFlightCourseRequests.delete(courseId);
      return;
    }

    this.courseCache.clear();
    this.inFlightCourseRequests.clear();
  }

  async getCourseById(courseId) {
    const cached = this._getCachedCourse(courseId);
    if (cached) {
      return cached;
    }

    const inFlight = this.inFlightCourseRequests.get(courseId);
    if (inFlight) {
      return inFlight;
    }

    const request = (async () => {
      const data = await this.client._invoke('getCourseById', { courseId });
      const course = data ? Course.fromJSON(data) : null;
      if (course) {
        this._setCachedCourse(courseId, course);
      }
      return course;
    })();

    this.inFlightCourseRequests.set(courseId, request);

    try {
      return await request;
    } finally {
      this.inFlightCourseRequests.delete(courseId);
    }
  }

  async getAllCourses() {
    const data = await this.client._invoke('getAllCourses');
    return (data || []).map(course => Course.fromJSON(course));
  }

  async getSectionById(courseId, sectionId) {
    const data = await this.client._invoke('getSectionById', { courseId, sectionId });
    return data ? Section.fromJSON(data) : null;
  }

  async getSectionsForCourse(courseId) {
    const data = await this.client._invoke('getSectionsForCourse', { courseId });
    return (data || []).map(section => Section.fromJSON(section));
  }

  async getQuestionById(courseId, questionId) {
    const data = await this.client._invoke('getQuestionById', { courseId, questionId });
    return data ? Question.fromJSON(data) : null;
  }

  async getQuestionsForSection(courseId, sectionId) {
    const course = await this.getCourseById(courseId);
    const section = course?.sections?.find(s => s.id === sectionId);

    if (section && Array.isArray(section.questions)) {
      return section.questions;
    }

    const data = await this.client._invoke('getQuestionsForSection', { courseId, sectionId });
    return (data || []).map(question => Question.fromJSON(question));
  }

  async getQuestionsForCourse(courseId) {
    const data = await this.client._invoke('getQuestionsForCourse', { courseId });
    const grouped = {};

    for (const [sectionId, questions] of Object.entries(data || {})) {
      grouped[sectionId] = (questions || []).map(question => Question.fromJSON(question));
    }

    return grouped;
  }

  async createCourse(courseData) {
    const data = await this.client._invoke('createCourse', { courseData });
    const createdCourse = Course.fromJSON(data);
    this._invalidateCourse();
    return createdCourse;
  }

  async updateCourse(courseId, courseData) {
    const data = await this.client._invoke('updateCourse', { courseId, courseData });
    const updatedCourse = Course.fromJSON(data);
    this._setCachedCourse(courseId, updatedCourse);
    return updatedCourse;
  }

  async deleteCourse(courseId) {
    const data = await this.client._invoke('deleteCourse', { courseId });
    this._invalidateCourse(courseId);
    return Boolean(data?.deleted);
  }

  async saveCourse(courseData) {
    const data = await this.client._invoke('saveCourse', { courseData });
    const savedCourse = Course.fromJSON(data);
    this._setCachedCourse(savedCourse?.id || courseData?.id, savedCourse);
    return savedCourse;
  }

  async upsertSection(courseId, sectionData) {
    const data = await this.client._invoke('upsertSection', { courseId, sectionData });
    const updatedCourse = Course.fromJSON(data);
    this._setCachedCourse(courseId, updatedCourse);
    return updatedCourse;
  }

  async deleteSection(courseId, sectionId) {
    const data = await this.client._invoke('deleteSection', { courseId, sectionId });
    const updatedCourse = Course.fromJSON(data);
    this._setCachedCourse(courseId, updatedCourse);
    return updatedCourse;
  }

  async reorderSections(courseId, orderedSectionIds) {
    const data = await this.client._invoke('reorderSections', { courseId, orderedSectionIds });
    const updatedCourse = Course.fromJSON(data);
    this._setCachedCourse(courseId, updatedCourse);
    return updatedCourse;
  }

  async upsertQuestion(courseId, sectionId, questionData) {
    const data = await this.client._invoke('upsertQuestion', { courseId, sectionId, questionData });
    const updatedCourse = Course.fromJSON(data);
    this._setCachedCourse(courseId, updatedCourse);
    return updatedCourse;
  }

  async deleteQuestion(courseId, sectionId, questionId) {
    const data = await this.client._invoke('deleteQuestion', { courseId, sectionId, questionId });
    const updatedCourse = Course.fromJSON(data);
    this._setCachedCourse(courseId, updatedCourse);
    return updatedCourse;
  }

  async reorderQuestions(courseId, sectionId, orderedQuestionIds) {
    const data = await this.client._invoke('reorderQuestions', { courseId, sectionId, orderedQuestionIds });
    const updatedCourse = Course.fromJSON(data);
    this._setCachedCourse(courseId, updatedCourse);
    return updatedCourse;
  }

  async upsertExample(courseId, sectionId, exampleData) {
    const data = await this.client._invoke('upsertExample', { courseId, sectionId, exampleData });
    const updatedCourse = Course.fromJSON(data);
    this._setCachedCourse(courseId, updatedCourse);
    return updatedCourse;
  }

  async deleteExample(courseId, sectionId, exampleId) {
    const data = await this.client._invoke('deleteExample', { courseId, sectionId, exampleId });
    const updatedCourse = Course.fromJSON(data);
    this._setCachedCourse(courseId, updatedCourse);
    return updatedCourse;
  }

  async getCourseEmailTemplates(courseId) {
    return this.client._invoke('getCourseEmailTemplates', { courseId });
  }

  async upsertCourseEmailTemplate(courseId, templateType, templateData) {
    return this.client._invoke('upsertCourseEmailTemplate', {
      courseId,
      templateType,
      templateData,
    });
  }

  async resetCourseEmailTemplate(courseId, templateType) {
    return this.client._invoke('resetCourseEmailTemplate', {
      courseId,
      templateType,
    });
  }

  async sendCourseStudyReminders(courseId, options = {}) {
    return this.client._invoke('sendCourseStudyReminders', {
      courseId,
      options,
    });
  }

  async getEmailDispatchLogs(filters = {}) {
    return this.client._invoke('getEmailDispatchLogs', {
      filters: {
        courseId: filters.courseId || '',
        status: filters.status || '',
        templateType: filters.templateType || '',
        provider: filters.provider || '',
        search: filters.search || '',
      },
      page: filters.page || 1,
      pageSize: filters.pageSize || 25,
    });
  }

  async getEmailDeliveryConfiguration() {
    return this.client._invoke('getEmailDeliveryConfiguration');
  }

  async upsertEmailDeliveryConfiguration(configData) {
    return this.client._invoke('upsertEmailDeliveryConfiguration', {
      configData,
    });
  }

  async resetEmailDeliveryConfiguration() {
    return this.client._invoke('resetEmailDeliveryConfiguration');
  }
}

export class APIProgressRepository extends IProgressRepository {
  constructor() {
    super();
    this.client = new BaseApiRepository('progress');
  }

  async getUserProgress(userId, courseId) {
    const data = await this.client._invoke('getUserProgress', { userId, courseId });
    return data ? UserProgress.fromJSON(data) : null;
  }

  async createUserProgress(userId, courseId) {
    const data = await this.client._invoke('createUserProgress', { userId, courseId });
    return UserProgress.fromJSON(data);
  }

  async updateUserProgress(progress) {
    const payload = typeof progress?.toJSON === 'function' ? progress.toJSON() : progress;
    const data = await this.client._invoke('updateUserProgress', { progress: payload });
    return UserProgress.fromJSON(data);
  }

  async getSectionProgress(userId, courseId, sectionId) {
    return this.client._invoke('getSectionProgress', { userId, courseId, sectionId });
  }

  async updateSectionProgress(userId, courseId, sectionId, progressData) {
    return this.client._invoke('updateSectionProgress', { userId, courseId, sectionId, progressData });
  }

  async getQuestionProgress(userId, courseId, questionId) {
    return this.client._invoke('getQuestionProgress', { userId, courseId, questionId });
  }

  async updateQuestionProgress(userId, courseId, questionId, progressData) {
    return this.client._invoke('updateQuestionProgress', { userId, courseId, questionId, progressData });
  }

  async saveAnswerHistory(userId, courseId, questionId, answerData) {
    return this.client._invoke('saveAnswerHistory', { userId, courseId, questionId, answerData });
  }

  async getAnswerHistory(userId, courseId, questionId) {
    return this.client._invoke('getAnswerHistory', { userId, courseId, questionId });
  }

  async getSectionQuestionProgress(userId, courseId, sectionId) {
    return this.client._invoke('getSectionQuestionProgress', { userId, courseId, sectionId });
  }

  async getAnswersForTag(userId, courseId, tagId) {
    return this.client._invoke('getAnswersForTag', { userId, courseId, tagId });
  }

  async recordSectionRemediation(userId, courseId, sectionId) {
    return this.client._invoke('recordSectionRemediation', { userId, courseId, sectionId });
  }
}

export class APISessionRepository extends ISessionRepository {
  constructor() {
    super();
    this.client = new BaseApiRepository('session');
  }

  async createSession(userId, courseId, sectionId) {
    const data = await this.client._invoke('createSession', { userId, courseId, sectionId });
    return Session.fromJSON(data);
  }

  async getSessionById(sessionId) {
    const data = await this.client._invoke('getSessionById', { sessionId });
    return data ? Session.fromJSON(data) : null;
  }

  async getActiveSession(userId, courseId) {
    const data = await this.client._invoke('getActiveSession', { userId, courseId });
    return data ? Session.fromJSON(data) : null;
  }

  async updateSession(session) {
    const payload = typeof session?.toJSON === 'function' ? session.toJSON() : session;
    const data = await this.client._invoke('updateSession', { session: payload });
    return Session.fromJSON(data);
  }

  async completeSession(sessionId) {
    const data = await this.client._invoke('completeSession', { sessionId });
    return Session.fromJSON(data);
  }

  async getSessionHistory(userId, courseId, limit = 10) {
    const data = await this.client._invoke('getSessionHistory', { userId, courseId, limit });
    return (data || []).map(session => Session.fromJSON(session));
  }
}

export class APIUserRepository extends IUserRepository {
  constructor() {
    super();
    this.client = new BaseApiRepository('user');
  }

  async getAllUsers(options = {}) {
    const data = await this.client._invoke('getAllUsers', options);
    return (data || []).map(user => User.fromJSON(user));
  }

  async getUserById(userId) {
    const data = await this.client._invoke('getUserById', { userId });
    return data ? User.fromJSON(data) : null;
  }

  async getUserByEmail(email) {
    const data = await this.client._invoke('getUserByEmail', { email });
    return data ? User.fromJSON(data) : null;
  }

  async createUser(userData, options = {}) {
    const data = await this.client._invoke('createUser', { userData, ...options });
    return User.fromJSON(data);
  }

  async updateUser(userId, updates) {
    const data = await this.client._invoke('updateUser', { userId, updates });
    return User.fromJSON(data);
  }

  async deleteUser(userId) {
    const data = await this.client._invoke('deleteUser', { userId });
    return Boolean(data?.deleted);
  }

  async getUsersByCourse(courseId) {
    const data = await this.client._invoke('getUsersByCourse', { courseId });
    return (data || []).map(user => User.fromJSON(user));
  }

  async enrollUserInCourse(userId, courseId, options = {}) {
    const data = await this.client._invoke('enrollUserInCourse', {
      userId,
      courseId,
      ...options,
    });
    return User.fromJSON(data);
  }

  async unenrollUserFromCourse(userId, courseId) {
    const data = await this.client._invoke('unenrollUserFromCourse', { userId, courseId });
    return User.fromJSON(data);
  }

  async suspendUserInCourse(userId, courseId) {
    const data = await this.client._invoke('suspendUserInCourse', { userId, courseId });
    return User.fromJSON(data);
  }

  async unsuspendUserInCourse(userId, courseId) {
    const data = await this.client._invoke('unsuspendUserInCourse', { userId, courseId });
    return User.fromJSON(data);
  }

  async createInitialAdmin(adminData) {
    const data = await this.client._invoke('createInitialAdmin', { adminData });
    return User.fromJSON(data);
  }

  async setUserPassword(userId, password, options = {}) {
    const data = await this.client._invoke('setUserPassword', {
      userId,
      password,
      ...options,
    });
    return User.fromJSON(data);
  }

  async requestPasswordReset(email, options = {}) {
    return this.client._invoke('requestPasswordReset', {
      email,
      ...options,
    });
  }

  async resetPasswordWithToken(token, password) {
    return this.client._invoke('resetPasswordWithToken', {
      token,
      password,
    });
  }

  async loginWithGoogle(idToken) {
    const data = await this.client._invoke('loginWithGoogle', { idToken });
    return User.fromJSON(data);
  }
}
